// Base URL for Process Creation service
// Configure in .env.local: REACT_APP_Create_Process_URL
import { aiFetch } from "./aiServiceAuth";
const CREATE_BASE_URL = process.env.REACT_APP_Create_Process_URL || "";
const Visualization_BASE_URL =
  process.env.REACT_APP_Visualize_Process_URL || "";

// Custom fetch for process creation API (doesn't go through main api.js to avoid double prefix)
const processCreateFetch = async (endpoint, options = {}) => {
  const headers = {
    Accept: "application/json",
    "Content-Type": "application/json",
    ...options.headers,
  };

  const response = await aiFetch(endpoint, { ...options, headers });

  if (!response.ok) {
    let message = `HTTP error! status: ${response.status}`;
    try {
      const ct = response.headers.get("content-type") || "";
      if (ct.includes("application/json")) {
        const errJson = await response.json();
        message = errJson.message || errJson.error || JSON.stringify(errJson);
      } else {
        const text = await response.text();
        if (text) message = text;
      }
    } catch (_) {}
    throw new Error(message);
  }

  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    return await response.json();
  }
  const text = await response.text();
  return text ? JSON.parse(text) : null;
};

// POST /processsequence/image?format=png
// input: { process_name: string, tasks: string[] }
// returns: object URL to PNG image rendering the task sequence
export async function getProcessSequenceImage(body, format = "png") {
  const endpoint = `${Visualization_BASE_URL}/processsequence/image?format=${encodeURIComponent(
    format || "png"
  )}`;
  const res = await aiFetch(endpoint, {
    method: "POST",
    headers: {
      Accept: "image/png,*/*",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `HTTP ${res.status}`);
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

// POST /processcreation
// body: { query: string }
// returns: { name: string, description: string }
export async function createProcessFromQuery(query) {
  const q = (query || "").trim();
  if (!q) throw new Error("Query is required");
  const endpoint = `${CREATE_BASE_URL}/processcreation`;
  return processCreateFetch(endpoint, {
    method: "POST",
    body: JSON.stringify({ query: q }),
  });
}

// POST /processtasks
// body: { name: string, description: string }
// returns: { task_names: string[], tasks: [{ name, description }] }
export async function createProcessTasks(name, description) {
  const n = (name || "").trim();
  const d = (description || "").trim();
  if (!n || !d) throw new Error("Name and description are required");
  const endpoint = `${CREATE_BASE_URL}/processtasks`;
  return processCreateFetch(endpoint, {
    method: "POST",
    body: JSON.stringify({ name: n, description: d }),
  });
}

// POST /taskjobs
// input shape:
// {
//   process_name: string,
//   process_description: string,
//   tasks: [{ name: string, description: string }]
// }
// returns: { jobs: [{ name, description, task_indices: number[] }] }
export async function createTaskJobs(body) {
  const endpoint = `${CREATE_BASE_URL}/taskjobs`;
  return processCreateFetch(endpoint, {
    method: "POST",
    body: JSON.stringify(body || {}),
  });
}

// POST /jobfunctions
// input shape:
// {
//   process_name: string,
//   process_description: string,
//   tasks: [{ name: string, description: string }],
//   jobs: [{ name: string, description: string, task_indices: number[] }]
// }
// returns: { functions: [{ name, description, job_indices: number[] }] }
export async function createJobFunctions(body) {
  const endpoint = `${CREATE_BASE_URL}/jobfunctions`;
  return processCreateFetch(endpoint, {
    method: "POST",
    body: JSON.stringify(body || {}),
  });
}

// POST /processgraph/image?format=png
// input shape matches the provided structure combining process_name, tasks, jobs, functions
// returns: an object URL to a PNG image for visualization
export async function getProcessGraphImage(body, format = "png") {
  const endpoint = `${Visualization_BASE_URL}/processgraph/image?format=${encodeURIComponent(
    format || "png"
  )}`;
  const res = await aiFetch(endpoint, {
    method: "POST",
    headers: {
      Accept: "image/png,*/*",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `HTTP ${res.status}`);
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}
